<script setup lang="ts">
import { computed, ref, watch } from 'vue';

interface Props {
  title?: string;
  modelValue?: boolean;
  defaultOpen?: boolean;
  hasBorder?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  modelValue: undefined,
  defaultOpen: false,
  hasBorder: true,
  disabled: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const internalOpen = ref(props.modelValue ?? props.defaultOpen);

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== undefined) {
      internalOpen.value = newValue;
    }
  }
);

const isOpen = computed({
  get: () => (props.modelValue !== undefined ? props.modelValue : internalOpen.value),
  set: (value) => {
    internalOpen.value = value;
    emit('update:modelValue', value);
  },
});

const border = computed(() => {
  return props.hasBorder ? 'border-b border-b-actions-primary' : '';
});

const buttonClasses = computed(() => [
  'flex items-center justify-between w-full p-7.5 transition-opacity',
  {
    'opacity-50 cursor-not-allowed': props.disabled,
  },
]);

const toggleAccordion = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    toggleAccordion();
  }
};

const chevronClass = computed(() => ({
  'rotate-180': isOpen.value,
  'rotate-0': !isOpen.value,
}));

const onBeforeEnter = (el: Element) => {
  (el as HTMLElement).style.height = '0';
};

const onEnter = (el: Element) => {
  window.requestAnimationFrame(() => {
    (el as HTMLElement).style.height = (el as HTMLElement).scrollHeight + 'px';
  });
};

const onAfterEnter = (el: Element) => {
  (el as HTMLElement).style.height = 'auto';
};

const onBeforeLeave = (el: Element) => {
  (el as HTMLElement).style.height = (el as HTMLElement).scrollHeight + 'px';
};

const onLeave = (el: Element) => {
  window.requestAnimationFrame(() => {
    (el as HTMLElement).style.height = '0';
  });
};
</script>

<template>
  <div class="flex flex-col w-full gap-3 mb-7">
    <div :class="['bg-white rounded-2xl', border]">
      <button
        :class="buttonClasses"
        :disabled="disabled"
        :aria-expanded="isOpen"
        :aria-disabled="disabled"
        @click="toggleAccordion"
        @keydown="handleKeydown"
      >
        <span class="text-xl font-semibold text-left text-black">{{ title }}</span>
        <i
          class="w-4.5 h-4.5 transition-transform duration-300 icon icon-chevron-down shrink-0 bg-black"
          :class="chevronClass"
          aria-hidden="true"
        ></i>
      </button>

      <Transition
        name="accordion"
        @before-enter="onBeforeEnter"
        @enter="onEnter"
        @after-enter="onAfterEnter"
        @before-leave="onBeforeLeave"
        @leave="onLeave"
      >
        <div v-if="isOpen" class="overflow-hidden">
          <div class="p-7.5 pt-0">
            <slot></slot>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.accordion-enter-active,
.accordion-leave-active {
  transition: height 0.3s ease;
}
</style>
